import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import aboutHero from "@/assets/about-hero.jpg";
import { attorneys } from "@/data/attorneys";
import { ArrowLeft, Scale, GraduationCap, Award, Briefcase, Globe, Download, Linkedin, Phone, MapPin, FileDown } from "lucide-react";
import EmailDisclaimerDialog from "@/components/EmailDisclaimerDialog";

const AttorneyBioPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const attorney = attorneys.find((a) => a.slug === slug);

  if (!attorney) {
    return (
      <Layout>
        <SEO title="Attorney Not Found | Vaughan McLean" description="This attorney profile is no longer available." noIndex />
        <PageHero backgroundImage={aboutHero} title="Attorney Not Found" subtitle="Our Team" />
        <section className="section-dark py-24">
          <div className="container mx-auto px-8 lg:px-16 text-center">
            <p className="text-muted-foreground font-body mb-8">The attorney you're looking for could not be found.</p>
            <Link to="/team" className="text-primary hover:underline font-body inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Our Team
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title={`${attorney.name} | Attorney | Vaughan McLean`}
        description={`Learn about ${attorney.name}, an attorney at Vaughan McLean specializing in ${attorney.practiceAreas.slice(0, 3).join(", ")}${attorney.practiceAreas.length > 3 ? ", and more" : ""}.`}
        ogType="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          mainEntity: {
            "@type": "Person",
            name: attorney.name,
            jobTitle: attorney.title || "Attorney",
            worksFor: { "@type": "LegalService", name: "Vaughan McLean" },
            ...(attorney.location ? { workLocation: attorney.location } : {}),
            ...(attorney.email ? { email: attorney.email } : {}),
            ...(attorney.phone ? { telephone: attorney.phone } : {}),
            url: `https://www.vaughanmclean.com/team/${attorney.slug}`,
          },
        }}
      />
      <PageHero backgroundImage={aboutHero} title={attorney.name} subtitle="Attorney Biography" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <Link
            to="/team"
            className="text-muted-foreground hover:text-primary transition-colors font-body inline-flex items-center gap-2 mb-12 text-sm tracking-[0.1em] uppercase"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Our Team
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              {attorney.photo ? (
                <div className="w-64 h-64 overflow-hidden mb-4">
                  <img src={attorney.photo} alt={attorney.name} className="w-full h-full object-cover object-top" />
                </div>
              ) : (
                <div className="w-64 h-64 border border-border flex items-center justify-center mb-4">
                  <span className="font-heading text-4xl text-primary">{attorney.initials}</span>
                </div>
              )}

              {attorney.title && (
                <p className="text-sm tracking-[0.1em] uppercase font-body text-primary mb-1">{attorney.title}</p>
              )}

              {attorney.location && (
                <div className="text-sm font-body text-muted-foreground mb-4 space-y-1">
                  {attorney.location.split(",").map((loc, idx) => (
                    <p key={idx} className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 shrink-0" />{loc.trim()}
                    </p>
                  ))}
                </div>
              )}

              {attorney.vcardUrl && (
                <a
                  href={attorney.vcardUrl}
                  download
                  className="flex items-center justify-center gap-2 text-sm font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-4 py-3 transition-colors tracking-[0.1em] uppercase mb-3 w-64"
                >
                  <Download className="w-4 h-4" />
                  Download vCard
                </a>
              )}

              {attorney.linkedinUrl && (
                <a
                  href={attorney.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-sm font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-4 py-3 transition-colors tracking-[0.1em] uppercase mb-3 w-64"
                >
                  <Linkedin className="w-4 h-4" />
                  LinkedIn Profile
                </a>
              )}

              {attorney.email && (
                <EmailDisclaimerDialog email={attorney.email} attorneyName={attorney.name} />
              )}

              {attorney.phone && (
                <a
                  href={`tel:${attorney.phone}`}
                  className="flex items-center justify-center gap-2 text-sm font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-4 py-3 transition-colors tracking-[0.1em] uppercase mb-8 w-64"
                >
                  <Phone className="w-4 h-4" />
                  {attorney.phone}
                </a>
              )}

              <button
                onClick={async () => {
                  try {
                    const { generateAttorneyPdf } = await import("@/utils/generateAttorneyPdf");
                    generateAttorneyPdf(attorney);
                  } catch (e) {
                    console.error("PDF generation failed:", e);
                  }
                }}
                className="flex items-center justify-center gap-2 text-sm font-body text-muted-foreground hover:text-primary hover:border-primary border border-border rounded px-4 py-3 transition-colors tracking-[0.1em] uppercase mb-8 w-64"
              >
                <FileDown className="w-4 h-4" />
                Download Bio (PDF)
              </button>

              {attorney.practiceAreas.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-4 flex items-center gap-2">
                    <Scale className="w-4 h-4" /> Practice Areas
                  </h3>
                  <ul className="space-y-2">
                    {attorney.practiceAreas.map((area) => (
                      <li key={area} className="text-foreground font-body text-sm">{area}</li>
                    ))}
                  </ul>
                </div>
              )}

              {attorney.licenses.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-4 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Licensed In
                  </h3>
                  <ul className="space-y-2">
                    {attorney.licenses.map((license) => (
                      <li key={license} className="text-foreground font-body text-sm">{license}</li>
                    ))}
                  </ul>
                </div>
              )}

              {attorney.education.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-4 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" /> Education
                  </h3>
                  <ul className="space-y-2">
                    {attorney.education.map((edu) => (
                      <li key={edu} className="text-foreground font-body text-sm">{edu}</li>
                    ))}
                  </ul>
                </div>
              )}

              {attorney.recognition.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-4 flex items-center gap-2">
                    <Award className="w-4 h-4" /> Recognition
                  </h3>
                  <ul className="space-y-2">
                    {attorney.recognition.map((item) => (
                      <li key={item} className="text-foreground font-body text-sm">{item}</li>
                    ))}
                  </ul>
                </div>
              )}


              {attorney.languages && attorney.languages.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-4 flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Languages
                  </h3>
                  <ul className="space-y-2">
                    {attorney.languages.map((lang) => (
                      <li key={lang} className="text-foreground font-body text-sm">{lang}</li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h2 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-6">Biography</h2>
              <div className="space-y-6 mb-16">
                {attorney.biography.map((paragraph, i) => (
                  <p key={i} className="text-foreground font-body leading-relaxed">{paragraph}</p>
                ))}
              </div>


              {attorney.representativeMatters.length > 0 && (
                <div className="mb-16">
                  <h2 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-6">Representative Matters</h2>
                  <ul className="space-y-4">
                    {attorney.representativeMatters.map((matter, i) => (
                      <li key={i} className="text-foreground font-body text-sm leading-relaxed pl-4 border-l border-border">{matter}</li>
                    ))}
                  </ul>
                </div>
              )}

              {attorney.admissions.length > 0 && (
                <div className="mb-16">
                  <h2 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-6">Bar & Court Admissions</h2>
                  <ul className="space-y-2">
                    {attorney.admissions.map((admission) => (
                      <li key={admission} className="text-foreground font-body text-sm">{admission}</li>
                    ))}
                  </ul>
                </div>
              )}

              {attorney.memberships.length > 0 && (
                <div className="mb-16">
                  <h2 className="font-heading text-sm tracking-[0.15em] uppercase text-muted-foreground mb-6">Memberships</h2>
                  <ul className="space-y-2">
                    {attorney.memberships.map((membership) => (
                      <li key={membership} className="text-foreground font-body text-sm">{membership}</li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AttorneyBioPage;
